import type { ChatEvent, ChatMessage } from './chat.ts';

/**
 * `completing` while the debrief is being written in the background;
 * `debrief_failed` when writing it gave up, and ending again retries it.
 */
export type RoleplaySessionStatus =
  | 'pending'
  | 'in_progress'
  | 'completing'
  | 'debrief_failed'
  | 'completed'
  | 'aborted';

/**
 * By when the queue has settled a debrief, written or `debrief_failed`: three
 * attempts of at most three minutes, their backoff and one stall recovery. A
 * session still `completing` after this lost its job, so ending it again may
 * take it over, and the screen stops waiting on it.
 */
export const ROLEPLAY_DEBRIEF_SETTLED_WITHIN_MS = 15 * 60_000;

export type RoleplayMode = 'chat' | 'voice';

export type RoleplayPersonaGender = 'female' | 'male';

/**
 * The lengths the API enforces, read by the form too. The two instruction
 * fields are the outliers: prose for the model, and they run long.
 */
export const ROLEPLAY_LIMITS = {
  title: 200,
  type_label: 120,
  situation: 4_000,
  persona_name: 120,
  persona_role: 200,
  persona_behavior: 4_000,
  persona_opening_line: 500,
  objective: 500,
  objectives: 5,
  extra_instructions: 8_000,
  feedback_instructions: 8_000,
} as const;

export interface RoleplayScenario {
  situation: string;
  persona: {
    name: string;
    role: string;
    behavior: string;
    /**
     * The exact words the persona opens with. Absent or empty lets the persona
     * open with a line of its own. Model material: never shown on a brief.
     */
    opening_line?: string;
    gender?: RoleplayPersonaGender;
  };
  learning_objectives: string[];

  /**
   * Authoritative facts the character must treat as ground truth (e.g. a new
   * law the model's own knowledge may have wrong). Injected into the prompt
   * only when present. Left empty by the normal product flow.
   */
  reference_facts?: string;

  /**
   * Extra scenario-level instructions for the model (e.g. how to handle
   * off-topic questions in a public demo). Injected only when present.
   */
  extra_instructions?: string;

  /**
   * The author's rules for the feedback afterwards. Only the debrief reads
   * them: a persona handed a grading brief starts grading mid-conversation.
   */
  feedback_instructions?: string;
}

/**
 * Who a roleplay belongs to. A library roleplay is authored by an admin and
 * offered to everyone in the organisation; a personal one belongs to the single
 * user it was made for (from chat today, from meeting prep later).
 */
export type RoleplayKind = 'library' | 'personal';

/** Publish state. Personal roleplays are always published. */
export type RoleplayStatus = 'draft' | 'published';

/** Where a personal roleplay came from. Null on library roleplays. */
export type RoleplaySource = 'chat' | 'prep';

export interface RoleplayDebrief {
  /**
   * False when the user barely took part: nothing is rated, and every other
   * field is empty. Absent on debriefs written before it was asked.
   */
  took_part?: boolean;
  recap: string;
  ratings: {
    objective: string;
    score: number;
    feedback: string;
  }[];
  standout: string;
  tip: string;
  /** Absent on debriefs written before the transcript had remarks. */
  remarks?: RoleplayDebriefRemark[];
}

/**
 * Wundo's remark on one moment of the practice conversation, shown under the
 * message it is about. `message_id` is that message's id in the transcript.
 */
export interface RoleplayDebriefRemark {
  message_id: string;
  remark: string;
  /** One sentence the user could have said instead; null for praise. */
  better_phrasing: string | null;
}

/** WebSocket URL on our API that runs the call on the voice pipeline. */
export type VoiceConnection = { transport: 'realtime'; url: string };

export interface RoleplaySessionResponse {
  id: string;
  /**
   * The roleplay this session is an attempt at. Null on sessions from before
   * roleplays became rows of their own. It is what the practice screen returns
   * to when the session ends — the feedback lives on the roleplay, not here.
   */
  roleplay_id: string | null;
  status: RoleplaySessionStatus;
  mode: RoleplayMode | null;
  scenario: RoleplayScenario;
  debrief: RoleplayDebrief | null;
  conversation_id: string | null;
  language: string;
  voice?: VoiceConnection;

  /**
   * Whether the reader arrived on a single-purpose link rather than a sign-in.
   *
   * This one screen is deliberately reachable both ways — from inside the app,
   * and from a link in a chat message by somebody who has no session — and the
   * two are not the same visit. A link visitor has this conversation and
   * nothing else: no library to return to, no next attempt to start, so the
   * screen keeps its feedback and its ending to itself rather than pointing at
   * an app they cannot open.
   */
  scoped_access: boolean;
}

/**
 * `completing` with no debrief yet: it is written in the background, and the
 * session is read again until it is `completed` or `debrief_failed`. `aborted`
 * when nobody spoke and there is none to write.
 */
export interface EndRoleplayResponse {
  id: string;
  status: RoleplaySessionStatus;
  debrief: RoleplayDebrief | null;
}

/** One card on the Oefenen page, with the caller's own practice history on it. */
export interface RoleplayCard {
  id: string;
  kind: RoleplayKind;
  status: RoleplayStatus;
  /** The authored title, or the situation when there is none. */
  title: string;
  /** Conversation type, e.g. "Functionering". Null on personal roleplays. */
  type_label: string | null;
  /**
   * The cohort this library roleplay was written for. Null means the whole
   * organisation. Only the admins who author the library are shown it — a
   * member never gets a card of a cohort other than their own anyway.
   */
  cohort: { id: string; title: string } | null;
  /** The track it was written for, read the same way. Never set with `cohort`. */
  track: { id: string; title: string } | null;
  situation: string;
  persona: {
    name: string;
    role: string;
    behavior: string;
  };
  learning_objectives: string[];
  source: RoleplaySource | null;
  created_at: string;
  /** Sessions the caller started for this roleplay. */
  attempt_count: number;
  last_practiced_at: string | null;
}

/**
 * One library roleplay as the admin who wrote it edits it.
 *
 * A separate shape from `RoleplayCard` because it carries the extra and the
 * feedback instructions — material only the model gets — which must never
 * reach the people practising. Read behind the same permission that lets someone edit.
 */
export interface RoleplayDetail {
  id: string;
  status: RoleplayStatus;
  title: string;
  type_label: string | null;
  /**
   * Who it is written for: one track, one cohort, or — both null — the whole
   * organisation. Never both at once.
   */
  track_id: string | null;
  cohort_id: string | null;
  situation: string;
  persona: {
    name: string;
    role: string;
    behavior: string;
    /** Empty when the author wrote none. */
    opening_line: string;
  };
  learning_objectives: string[];
  /** Empty when the author wrote none. */
  extra_instructions: string;
  /** Empty when the author wrote none. */
  feedback_instructions: string;
}

export interface RoleplayListResponse {
  library: RoleplayCard[];
  personal: RoleplayCard[];
  /** Whether the caller may author, edit and publish library roleplays. */
  can_manage: boolean;
}

/** One past session of a roleplay, for the feedback modal. */
export interface RoleplayAttempt {
  id: string;
  status: RoleplaySessionStatus;
  mode: RoleplayMode | null;
  created_at: string;
  completed_at: string | null;
  debrief: RoleplayDebrief | null;
}

export interface RoleplayAttemptsResponse {
  data: RoleplayAttempt[];
}

/**
 * What deleting a roleplay would take with it, read before the confirm.
 *
 * Both numbers count the whole organisation's practice, not the caller's own:
 * the person deleting is being told what they are taking away from everybody
 * else. Zero on both is a scenario nobody has practised yet.
 */
export interface RoleplayDeleteImpactResponse {
  session_count: number;
  user_count: number;
}

export interface StartRoleplayResponse {
  id: string;
  status: RoleplaySessionStatus;
}

/** What a message sent into a written practice conversation answers with. */
export interface RoleplaySendResponse {
  message: ChatMessage;
}

/**
 * The character judges the conversation has run its course.
 *
 * Only a written conversation hears this, here, because the tool call it comes
 * from is Wundo's workings and never travels as a message.
 */
export interface RoleplaySuggestEndEvent {
  type: 'suggest_end';
}

/**
 * Everything a practice session's room carries.
 *
 * A written practice conversation is a conversation like the one in the chat
 * drawer, so it speaks the drawer's own events — the same shapes, through the
 * same serializer — plus the one thing only a roleplay has to say.
 */
export type RoleplayEvent = ChatEvent | RoleplaySuggestEndEvent;

/** The websocket room carrying one practice session's events. */
export function roleplayRoomName(sessionId: string): string {
  return `roleplay:${sessionId}`;
}
